import { Loader2, RotateCcw, Sparkles } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'
import { ValueMappingEditor } from '@/pages/AudiencesNewPage/components/mapping/ValueMappingEditor'
import { identityFieldLabel } from '../lib/required-fields-ui'
import type {
  IdentityFieldSummary,
  IdentityValueMapWorkspace,
} from '@/types/components/audiences/import-session'

interface IdentityValueMapPanelProps {
  field: IdentityFieldSummary
  workspace: IdentityValueMapWorkspace | null
  isPatching: boolean
  onDirtyChange: (dirty: boolean) => void
  onBusyChange: (busy: boolean) => void
  onApply: (sourceColumn: string, valueMap: Record<string, string>) => void
}

export function IdentityValueMapPanel({
  field,
  workspace,
  isPatching,
  onDirtyChange,
  onBusyChange,
  onApply,
}: IdentityValueMapPanelProps) {
  const { t } = useTranslation('app')
  const fieldLabel = identityFieldLabel(field.canonicalId, field.label, t)
  return (
    <div className="space-y-6">
      {workspace?.sourceColumn ? (
        <ValueMappingEditor
          canonicalId={workspace.canonicalId}
          variableLabel={fieldLabel}
          sourceColumn={workspace.sourceColumn}
          initialValueMap={workspace.currentValueMap ?? {}}
          distinctValues={workspace.distinctValues}
          valueLabels={workspace.valueLabels ?? null}
          previewRows={workspace.previewRows}
          allColumnNames={workspace.allColumnNames}
          disabled={isPatching}
          applyLabel={t('audienceCreate.defineField', { field: fieldLabel })}
          onDirtyChange={onDirtyChange}
          onBusyChange={onBusyChange}
          onApply={onApply}
          renderToolbarButtons={({
            onAutoDetect,
            onAutoFill,
            llmLoading,
            unmappedCount,
            isBusy,
          }) => (
            <div className="ml-auto flex items-end gap-3">
              <button
                type="button"
                onClick={onAutoDetect}
                disabled={isBusy}
                className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium border-input-focus text-foreground dark:text-input-focus hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t('audienceCreate.identityReDetect')}
              </button>
              {unmappedCount > 0 ? (
                <button
                  type="button"
                  onClick={onAutoFill}
                  disabled={isBusy}
                  className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium border-input-focus text-foreground dark:text-input-focus hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {llmLoading ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {t('audienceCreate.analyzingEllipsis')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5" />
                      {t('audienceCreate.autoFillWithAi')}
                    </>
                  )}
                </button>
              ) : null}
            </div>
          )}
          renderActions={({
            onApply: apply,
            onUndo,
            isApplying,
            isDirty,
            isBusy,
          }) => (
            <div className="flex items-center justify-center gap-4 pt-5">
              <Button
                type="button"
                variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
                className={cn('h-10 rounded-md px-4 tracking-tight', AUDIENCE_WIZARD_BUTTON_CLASS)}
                onClick={apply}
                disabled={isBusy}
              >
                {isApplying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('audienceCreate.saving')}
                  </>
                ) : (
                  t('audienceCreate.saveChanges')
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-10 rounded-md border px-4 text-base font-medium tracking-tight border-input-focus text-foreground dark:text-input-focus hover:bg-input-focus/10"
                onClick={onUndo}
                disabled={isBusy || !isDirty}
              >
                <RotateCcw className="mr-2 h-3.5 w-3.5" />
                {t('audienceCreate.undoChanges')}
              </Button>
            </div>
          )}
        />
      ) : (
        <div className="rounded-2xl border border-dashed border-border/70 px-6 py-8 text-sm text-muted-foreground">
          {field.currentSourceColumn
            ? t('audienceCreate.valueMappingLoading')
            : t('audienceCreate.noValueMappingYet')}
        </div>
      )}
    </div>
  )
}
